import {
  beSendGoDeleteSending,
  beSendGoGetEmailSendingDetails,
  beSendGoGetReadySending,
  beSendGoSetStandBySendingToReady,
  beSendGoStopSending,
} from 'Api/beSendGoApi'

import {
  beABTestGetABTestConfigById,
  beABTestGetAbTestIdByInvio,
} from 'Api/beABTestApi'
import { BeListApiGetList } from 'Api/BeListApi'

import { beListApiGetListsLazy } from 'Api/beListApiTS'

import { beMessMessagesDetailByIds } from 'Api/BeMessApi'
import { campaignsBuilderGetEmailCampaignByIdMessage } from 'Api/campaignsBuilderApi'

import {
  evalCondition,
  getLang,
  STORE_KEY_ACCOUNT,
  STORE_KEY_LIST,
  STORE_KEY_USER_LISTS,
  storeGet,
} from 'mailup-react-helpers'

import {
  MESSAGE_STR_TYPE_CONTENTSCAMPAIGN,
  SENDING_SOURCE_ABTEST_TEST,
  SENDING_SOURCE_ABTEST_WINNER,
} from 'Constants'

import { fromUTCtoZoneFormatted, zones } from 'Helpers/dates'

import { beFiltersGetFilter, FILTER_TYPE_ENGAGEMENT } from 'Api/beFiltersApi'
import {
  BEQUEUE_CONFIG_ACTION_MAIL,
  BEQUEUE_CONFIG_EXCLUDE,
  BEQUEUE_CONFIG_FILTER_COND_OR,
  BEQUEUE_CONFIG_INCLUDE,
  BEQUEUE_CONFIG_TYPE_EVERYBODY,
  beQueueSimulateConfigurationProcessing,
} from 'Api/beQueueApi'
import { beRecipientGetGroupDetails } from 'Api/beRecipientApi'
import { SENDING_SOURCE_SCHEDULED } from 'Constants/'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import getIntl from 'Helpers/intl'

dayjs.extend(utc)
dayjs.extend(timezone)

const isABTest = (source) =>
  source === SENDING_SOURCE_ABTEST_TEST ||
  source === SENDING_SOURCE_ABTEST_WINNER

export async function getCampaignsInfo(messagesMap) {
  const cmpMessages = Object.entries(messagesMap).filter(
    ([IdMessage, { MessageType }]) =>
      MessageType === MESSAGE_STR_TYPE_CONTENTSCAMPAIGN,
  )

  const campaignCalls = cmpMessages.map(([IdMessage, { IdList }]) =>
    campaignsBuilderGetEmailCampaignByIdMessage(IdMessage, IdList),
  )

  const campaigns = await Promise.all(campaignCalls)

  return cmpMessages.reduce(
    (acc, [IdMessage], index) => ({
      ...acc,
      [IdMessage]: {
        Id: campaigns[index].Id,
        Name: campaigns[index].Name,
        IdSource: campaigns[index].IdSource,
      },
    }),
    {},
  )
}

/**
 * @param {{Id: Number, Source: String}[]} items
 * @returns {Map} map of Id sending: ABTest config
 */
export async function getABTestsInfo(items) {
  const abtestSendingsIds = items
    .filter(({ Source }) => isABTest(Source))
    .map(({ Id }) => Id)

  const abtestIds = await Promise.all(
    abtestSendingsIds.map((idSending) => beABTestGetAbTestIdByInvio(idSending)),
  )

  const abtestConfigs = await Promise.all(
    abtestIds.map((idABTest) =>
      idABTest !== -1 ? beABTestGetABTestConfigById(idABTest) : undefined,
    ),
  )

  return abtestConfigs.reduce(
    (acc, config, index) => ({
      ...acc,
      [abtestSendingsIds[index]]: {
        ABTestID: config ? config.Info.ABTestID : undefined,
      },
    }),
    {},
  )
}

export async function getListsTimezones(listIds) {
  const result = await Promise.all(
    listIds.map((idList) => BeListApiGetList(idList)),
  )

  return result.reduce(
    (acc, { IdList, TimeZoneCode }) => ({ ...acc, [IdList]: TimeZoneCode }),
    {},
  )
}

export const sortSendingItems = ({ StartDateUTC: a }, { StartDateUTC: b }) =>
  a > b ? 1 : a < b ? -1 : 0

export async function fetchSendings(idLists, dateFrom, dateTo) {
  const userLists = storeGet(STORE_KEY_USER_LISTS)
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)
  const lang = getLang()
  const { GracePeriod } = storeGet(STORE_KEY_ACCOUNT)

  const canReschedule = evalCondition('NOT uo:NOSEND AND NOT uo:NOSCHED')
  const canStop = evalCondition('uo:GEST OR NOT uo:NOSTOPSENDING')

  const { Items } = await beSendGoGetReadySending({
    idLists,
    dateFrom: dateFrom.replace(/\.\d{3}Z$/, 'Z'),
    dateTo: dateTo.replace(/\.\d{3}Z$/, 'Z'),
  })

  const items = Items.filter((o) => userLists.includes(o.IdList)).sort(
    sortSendingItems,
  )

  const messageIds = items.map(({ IdMessage }) => IdMessage)

  const allMessagesDetails =
    messageIds.length > 0
      ? await beMessMessagesDetailByIds(messageIds, true)
      : []

  const messagesMap = allMessagesDetails.reduce(
    (acc, { Id, IdList, MessageType, Subject }) => ({
      ...acc,
      [Id]: { IdList, MessageType, Subject },
    }),
    {},
  )

  const listsIds = Array.from(new Set(items.map(({ IdList }) => IdList)))
  const listsTimezones = await getListsTimezones(listsIds)

  const campaignsMap = await getCampaignsInfo(messagesMap)
  const ABTestsMap = await getABTestsInfo(items)

  const now = Date.now()

  return items.map(
    ({
      Id,
      IdMessage,
      IdList,
      Source,
      StartDateUTC,
      TimeZoneInternalCode,
      Queued,
      IsPartial,
    }) => ({
      id: Id,
      idMessage: IdMessage,
      idList: IdList,
      idABTest: ABTestsMap[Id] ? ABTestsMap[Id].ABTestID : undefined,
      title:
        messagesMap[IdMessage].MessageType === MESSAGE_STR_TYPE_CONTENTSCAMPAIGN
          ? campaignsMap[IdMessage].Name
          : messagesMap[IdMessage].Subject,
      source: Source,
      campaign: { ...campaignsMap[IdMessage] },
      dateFormatted: StartDateUTC
        ? fromUTCtoZoneFormatted(StartDateUTC, TimeZoneCode, 'YYYY-MM-DD', lang)
        : undefined,
      timeFormatted: StartDateUTC
        ? fromUTCtoZoneFormatted(StartDateUTC, TimeZoneCode, 'HH:mm', lang)
        : undefined,
      date: StartDateUTC,
      scheduledFor: StartDateUTC
        ? fromUTCtoZoneFormatted(
            StartDateUTC,
            TimeZoneInternalCode || listsTimezones[IdList],
            'YYYY-MM-DD HH:mm',
            lang,
          )
        : undefined,
      timezone: `${TimeZoneInternalCode || listsTimezones[IdList]}`,
      timezoneFormatted: StartDateUTC
        ? fromUTCtoZoneFormatted(
            StartDateUTC,
            TimeZoneInternalCode || listsTimezones[IdList],
            'UTCZ',
            lang,
          )
        : undefined,
      recipientsQueued: Queued,
      isPartial: IsPartial,
      listIndex: listsIds.findIndex((v) => v === IdList),
      actions: {
        segmentation: !isABTest(Source),
        reschedule:
          !isABTest(Source) &&
          canReschedule &&
          Source === SENDING_SOURCE_SCHEDULED &&
          GracePeriod !== 2 &&
          new Date(StartDateUTC).getTime() - now > 60000,
        manageABTest: isABTest(Source),
        stop:
          canStop &&
          !isABTest(Source) &&
          messagesMap[IdMessage].MessageType !==
            MESSAGE_STR_TYPE_CONTENTSCAMPAIGN &&
          GracePeriod !== 2,
        stopCampaign:
          !isABTest(Source) &&
          messagesMap[IdMessage].MessageType ===
            MESSAGE_STR_TYPE_CONTENTSCAMPAIGN &&
          GracePeriod !== 2,
      },
    }),
  )
}

export const toFirstDOW = (date) =>
  (date.day() === 0 ? date : date.clone().day(0))
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)

export const toLastDOW = (date) =>
  (date.day() === 6 ? date : date.clone().day(6))
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)

export const getTodayDate = () => {
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)

  const tz = zones[TimeZoneCode]

  const today = dayjs
    .tz(Date.now(), tz)
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)
    .format('YYYY-MM-DD')

  return today
}

/**
 *
 * @param {string} startDate current calendar date YYYY-MM-DD
 * @param {string} today today date YYYY-MM-DD
 * @param {string} calendarAction 'next' or 'prev'
 * @returns
 */
export const evalApplyDate = (startDate, today, calendarAction) => {
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)

  const tz = zones[TimeZoneCode]

  const baseDate = dayjs
    .tz(startDate || today, tz)
    .set('date', 1)
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)

  const dateFrom = calendarAction
    ? calendarAction === 'next'
      ? baseDate.clone().add(1, 'month').date(1)
      : baseDate.clone().subtract(1, 'month').date(1)
    : baseDate

  const dateTo = dateFrom.clone().endOf('month')

  const calendarDateFrom = toFirstDOW(dateFrom)
  const calendarDateTo = toLastDOW(dateTo)

  //
  const days = []
  let curr = calendarDateFrom

  while (curr.format('YYYYMMDD') <= calendarDateTo.format('YYYYMMDD')) {
    const currfmt = curr.format('YYYY-MM-DD')

    days.push({
      isoDate: curr.toISOString(),
      date: curr.format('YYYY-MM-DD'),
      dateFormatted: curr.format('YYYY-MM-DD'),
      day: curr.format('D'),
      currentMonth: curr.format('YYYYMM') === dateFrom.format('YYYYMM'),
      currentDay: currfmt === today,
      isPast: currfmt < today,
    })
    curr = curr.clone().add(1, 'day')
  }

  const currMonthFtm = dateFrom.format('YYYYMM')
  const todayMonthFmt = today.substring(0, 7).replace('-', '')

  return {
    dateFrom: calendarDateFrom.subtract(3, 'day').toDate(),
    dateTo: calendarDateTo.add(3, 'day').toDate(),
    currentMonth: dateFrom.format('YYYY-MM-DD'),
    days,
    canPrev: currMonthFtm > todayMonthFmt,
    timezoneFormatted: dayjs.tz(today, tz).format('UTCZ'),
  }
}

export async function getAllLists() {
  const { Items } = await beListApiGetListsLazy({
    force: true,
    pageSize: 0,
    pageNumber: 0,
    orderBy: 'Id',
    orderDescending: false,
  })

  return Items.map(({ IdList, Name }) => ({
    value: IdList,
    label: `${IdList} - ${Name}`,
  }))
}

export const deleteSending = beSendGoDeleteSending
export const stopSending = beSendGoStopSending

export async function getSendingSegmentation(idSending) {
  const hasEngagement = evalCondition('co:DELSTATS OR co:ENGAGEMENT')
  const hasSimulation = evalCondition('co:DELTACALCSEGM')

  const {
    IdMessage,
    IdSendStop,
    Groups,
    ExcludeGroups,
    Filtri,
    Queued: queued,
  } = await beSendGoGetEmailSendingDetails(idSending)

  const groupsIds = Groups.split(',')
    .filter((o) => !!o)
    .map((o) => Number.parseInt(o))

  const groupsCalls = groupsIds.map((idGroup) => {
    return idGroup !== -1 && idGroup !== 0
      ? beRecipientGetGroupDetails(idGroup)
      : {
          Id: idGroup,
        }
  })

  const groups = (await Promise.all(groupsCalls)).map((group, index) => {
    return group
      ? {
          id: group.Id,
          name:
            group.Id !== 0
              ? group.Name
              : getIntl().formatMessage({ id: 'Load.Filters.NoGroupOption' }),
        }
      : {
          id: groupsIds[index],
          deleted: true,
        }
  })

  const excludedGroupsIds = ExcludeGroups.split(',')
    .filter((o) => !!o)
    .map((o) => Number.parseInt(o))

  const excludedGroupsCalls = excludedGroupsIds.map((idGroup) =>
    beRecipientGetGroupDetails(idGroup),
  )

  const excludedGroups = (await Promise.all(excludedGroupsCalls)).map(
    (group, index) => {
      return group
        ? {
            id: group.Id,
            name: group.Name,
          }
        : {
            id: excludedGroupsIds[index],
            deleted: true,
          }
    },
  )

  const filtersIds = Filtri.split(',')
    .filter((o) => !!o)
    .map((o) => Number.parseInt(o))

  const filtersCalls = filtersIds.map((idFilter) =>
    beFiltersGetFilter(idFilter),
  )

  const filters = (await Promise.all(filtersCalls))
    .map((filter, index) => {
      return filter
        ? {
            id: filter.Id,
            name: filter.Name,
            type: filter.Type,
          }
        : {
            id: filtersIds[index],
            deleted: true,
          }
    })
    .filter(({ type }) => {
      return type !== FILTER_TYPE_ENGAGEMENT || hasEngagement
    })

  const anyDeleted = []
    .concat(groups, excludedGroups, filters)
    .some((o) => !!o.deleted)

  return {
    idMessage: IdMessage,
    simulationEnabled: hasSimulation && IdSendStop === 0 && !anyDeleted,
    stopped: IdSendStop !== 0,
    queued,
    groups,
    excludedGroups,
    filters,
  }
}

export async function simulateSending(
  idSending,
  groups,
  excludedGroups,
  filters,
) {
  const { IdList } = await beSendGoGetEmailSendingDetails(idSending)

  const config = {
    IdList,
    IdInvio: 0,
    Action: BEQUEUE_CONFIG_ACTION_MAIL,
    Type: BEQUEUE_CONFIG_TYPE_EVERYBODY,
    FiltersConditionRule: BEQUEUE_CONFIG_FILTER_COND_OR,
    AsyncQueueFilter: filters.map(({ id: IdFilter }) => ({ IdFilter })),
    AsyncQueueGroup: [
      ...groups.map(({ id: IdGroup }) => ({
        IdGroup,
        Action: BEQUEUE_CONFIG_INCLUDE,
      })),
      ...excludedGroups.map(({ id: IdGroup }) => ({
        IdGroup,
        Action: BEQUEUE_CONFIG_EXCLUDE,
      })),
    ],
  }

  const result = await beQueueSimulateConfigurationProcessing(config)

  return result
}

export async function setSendingScheduled(
  idSending,
  DateStart,
  TimeZoneInternalCode,
) {
  return beSendGoSetStandBySendingToReady(idSending, {
    StartDate: DateStart.toISOString(),
    TimeZoneInternalCode,
    Source: SENDING_SOURCE_SCHEDULED,
  })
}

export const updateSendingItem = (o, dateStart, ScheduleTimeZoneCode) => {
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)
  return {
    ...o,
    dateFormatted: fromUTCtoZoneFormatted(
      dateStart,
      TimeZoneCode,
      'YYYY-MM-DD',
    ),
    timeFormatted: fromUTCtoZoneFormatted(dateStart, TimeZoneCode, 'HH:mm'),
    date: dateStart.toISOString(),
    scheduledFor: fromUTCtoZoneFormatted(
      dateStart,
      ScheduleTimeZoneCode,
      'YYYY-MM-DD HH:mm',
    ),
    timezone: ScheduleTimeZoneCode,
    timezoneFormatted: fromUTCtoZoneFormatted(
      dateStart,
      ScheduleTimeZoneCode,
      'UTCZ',
    ),
  }
}
